import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Loader2, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Privacy Score Dashboard',
    'Protection Guides',
    'AI Privacy Assistant',
    'Community Access'
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#00FF94]" />
            </div>
          </Link>
          <h1 className="font-heading font-bold text-2xl text-white mb-2">
            Create Your Account
          </h1>
          <p className="text-gray-500">
            Start protecting your digital privacy today
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-gray-400 mb-2 block">Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="bg-[#171717] border-[#262626] text-white placeholder:text-gray-600 focus:border-[#00FF94]"
                data-testid="register-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-400 mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="bg-[#171717] border-[#262626] text-white placeholder:text-gray-600 focus:border-[#00FF94]"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-400 mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-[#171717] border-[#262626] text-white placeholder:text-gray-600 focus:border-[#00FF94] pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">Minimum 6 characters</p>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full btn-primary h-11"
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Account
            </Button>
          </form>

          {/* Benefits */}
          <div className="mt-6 pt-6 border-t border-[#262626]">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Free plan includes:</p>
            <ul className="grid grid-cols-2 gap-2">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2 text-xs text-gray-400">
                  <Check className="w-3 h-3 text-[#00FF94]" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#00FF94] hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
